import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { InstallApp } from "../components/InstallApp";
import { Activity, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const rawNext = new URLSearchParams(location.search).get("next") || "";
  const next = /^\/(?![\/\\])/.test(rawNext) ? rawNext : isLogin ? "/" : "/premium";
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const email = form.email.trim();
      if (isLogin) await login(email, form.password);
      else await register(email, form.password, form.name.trim());
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, type = "text", extra = {}) => (
    <label className="block">
      <span className="label text-[10px]">{label}</span>
      <span className="relative block mt-1.5">
        <input
          type={key === "password" && showPwd ? "text" : type}
          value={form[key]}
          onChange={(e) => {
            setForm({ ...form, [key]: e.target.value });
            setError("");
          }}
          data-testid={`auth-${key}-input`}
          className={`w-full bg-[#0c0c0c] border border-[#27272A] px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-[#39FF14] transition-colors ${key === "password" ? "pr-12" : ""}`}
          {...extra}
        />
        {key === "password" && (
          <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label={showPwd ? "Nascondi password" : "Mostra password"}
            data-testid="auth-password-toggle" className="absolute right-0 top-0 h-full px-4 text-[#71717A] hover:text-white transition-colors">
            {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </span>
    </label>
  );

  return (
    <div className="min-h-screen grid place-items-center px-6 py-16">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-3 mb-10" data-testid="auth-logo-link">
          <div className="w-10 h-10 bg-[#39FF14] text-black grid place-items-center" style={{ clipPath: "polygon(0 0, 100% 0, 85% 100%, 0 100%)" }}>
            <Activity strokeWidth={2.5} size={20} />
          </div>
          <div className="heading text-xl font-black">GOAL.AI</div>
        </Link>
        <h1 className="heading text-4xl sm:text-5xl font-black tracking-tighter">{isLogin ? "Accedi" : "Crea account"}</h1>
        <p className="text-sm text-[#A1A1AA] mt-2">
          {isLogin
            ? "Accedi per vedere partite, formazioni, statistiche e pronostici AI."
            : "Crea il tuo account e attiva Premium a 5,99 €/mese. Disdici quando vuoi."}
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4 surface-raised p-6" data-testid={`${mode}-form`}>
          {!isLogin && field("name", "Nome", "text", { autoComplete: "name" })}
          {field("email", "Email", "email", { required: true, autoComplete: "email", autoCapitalize: "none", autoCorrect: "off", spellCheck: false, inputMode: "email" })}
          {field("password", "Password", "password", { required: true, minLength: isLogin ? 1 : 8, autoComplete: isLogin ? "current-password" : "new-password" })}
          {!isLogin && <p className="text-[11px] text-[#71717A]">Minimo 8 caratteri.</p>}
          {error && <div className="text-sm text-[#FF3B30]" data-testid="auth-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid={`${mode}-submit-button`}
            className="w-full bg-[#39FF14] text-black font-black uppercase tracking-wider text-sm py-3 hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />} {isLogin ? "Accedi" : "Registrati"}
          </button>
        </form>
        <p className="text-sm text-[#A1A1AA] mt-6 text-center">
          {isLogin ? "Non hai un account? " : "Hai già un account? "}
          <Link to={`${isLogin ? "/register" : "/login"}${location.search}`} className="text-[#39FF14] font-bold" data-testid="auth-switch-link">
            {isLogin ? "Registrati" : "Accedi"}
          </Link>
        </p>
        <div className="mt-8"><InstallApp /></div>
      </div>
    </div>
  );
}
